import { Badge, Typography } from '@lucid/lego';
import { formatDate } from '../../../../utils/tools';

export const formatCaseTitle = (caseId: string | null | undefined) => {
  if (!caseId) return 'xxxxxxxxxxxx';
  const shortId = caseId.slice(-12);
  return `${shortId}`;
};

export interface FseCase {
  customerCaseIdentifier: string;
  id: string;
  caseNumber: string;
  vin: string;
  vehicleModel: string;
  status: string;
  priority: string;
  ownerName: string;
  escalationType: string;
  createdDatetime: string;
  description: string;
  techSupportIdentifier: string;
  technicianName: string;
  vehicleDetails: {
    [key: string]: string;
  };
  customerName: string;
  mileageInMiles: number;
  mileageOutMiles: number;
}

export type CaseDetailType = FseCase;

const getStatusBadgeColor = (status: string): 'error' | 'warning' | 'informative' | 'success' => {
  if (!status) return 'informative';
  switch (status.toLowerCase()) {
    case 'open':
      return 'error';
    case 'in-progress':
      return 'warning';
    case 'resolved':
      return 'informative';
    default:
      return 'informative';
  }
};

const getPriorityBadgeColor = (
  priority: string
): 'error' | 'warning' | 'informative' | 'success' => {
  if (!priority) return 'informative';
  switch (priority.toLowerCase()) {
    case 'high':
      return 'error';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'informative';
  }
};

const formatStatus = (status: string | null | undefined): string => {
  if (!status) return '';
  return status
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

interface CreateColumnsParams {
  theme: 'light' | 'dark';
  onEditClick: (record: FseCase) => void;
}

export const createColumns = ({ theme, onEditClick }: CreateColumnsParams) => [
  {
    key: 'techSupportIdentifier',
    title: 'Case Number',
    width: '140px',
    sortable: true,
    render: (_: unknown, record: FseCase) => (
      <Typography
        variant="body"
        size="small"
        theme={theme}
        color="blue"
        onClick={() => onEditClick(record)}
      >
        {formatCaseTitle(record?.techSupportIdentifier)}
      </Typography>
    ),
  },
  {
    key: 'ownerName',
    title: 'Owner',
    width: '150px',
    sortable: true,
    render: (_: unknown, record: FseCase) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {record?.customerName || 'N/A'}
      </Typography>
    ),
  },
  {
    key: 'escalationType',
    title: 'Support Type',
    width: '180px',
    sortable: true,
    render: (_: unknown, record: FseCase) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {record?.escalationType || 'N/A'}
      </Typography>
    ),
  },
  {
    key: 'vin',
    title: 'VIN',
    width: '180px',
    sortable: true,
    render: (_: unknown, record: FseCase) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {record?.vin || 'N/A'}
      </Typography>
    ),
  },
  {
    key: 'vehicleModel',
    title: 'Car Model',
    width: '160px',
    sortable: true,
    render: (_: unknown, record: FseCase) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {record?.vehicleModel || 'N/A'}
      </Typography>
    ),
  },
  {
    key: 'status',
    title: 'Status',
    width: '130px',
    align: 'center',
    sortable: true,
    render: (_: unknown, record: FseCase) => (
      <Badge
        label={formatStatus(record.status)}
        color={getStatusBadgeColor(record.status)}
        type="secondary"
        theme={theme}
      />
    ),
  },
  {
    key: 'priority',
    title: 'Priority',
    width: '120px',
    align: 'center',
    sortable: true,
    render: (_: unknown, record: FseCase) => (
      <Badge
        label={record.priority.charAt(0).toUpperCase() + record.priority.slice(1) || 'N/A'}
        color={getPriorityBadgeColor(record.priority)}
        type="secondary"
        theme={theme}
      />
    ),
  },
  {
    key: 'createdDatetime',
    title: 'Date Created',
    width: '140px',
    sortable: true,
    render: (_: unknown, record: FseCase) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {record?.createdDatetime ? formatDate(record?.createdDatetime) : 'N/A'}
      </Typography>
    ),
  },
  {
    key: 'description',
    title: 'Description',
    width: '250px',
    render: (_: unknown, record: FseCase) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {record?.description || 'N/A'}
      </Typography>
    ),
  },
];

export const createFSEDetail = (record: FseCase): CaseDetailType => {
  return {
    ...record,
  };
};
